
import { AsyncTask, AsyncTaskFormat, AsyncTaskProcess, AsyncTaskStatus, AsynTaskHeaders } from '@/types/catalogs/AsyncTask'
import { APIErrorFormat } from '@/types/Errors'
import Vue, { PropType } from 'vue'
import { CustomSearch, SearchOrigin } from '@/types/catalogs/CustomSearch'
import CatalogsFillsService from '@/services/CatalogsFillsService'
import { FillgridFilters } from '@/types/Fillgrid'
import { UNDEFINED_ENTITY_ID } from '@/types/Catalogs'
import { NUMBER, STRING } from '@/types/GeneralConstants'
import LoadingMask from '@/components/generic/LoadingMask.vue'
import { AttachmentObject, AttachmentFormat, VariableFill } from '@/types/catalogs/Attachment'

export default Vue.extend({
  components: {
    LoadingMask,
    AttachmentsSelection: () => import('@/components/generic/AttachmentsSelection.vue')
  },
  data () {
    return {
      FILLS_MAX_LIMIT: 100000,
      FILLS_WITH_ATTACHMENTS_MAX_LIMIT: 20000,
      UNDEFINED_ENTITY_ID: UNDEFINED_ENTITY_ID,
      AsyncTaskProcess: AsyncTaskProcess,
      formats: Object.values(AsyncTaskFormat).filter(format => ['CSV', 'TSV'].includes(format)),
      headers: Object.values(AsynTaskHeaders),
      searchValues: {} as CustomSearch,
      searchCreated: undefined as undefined | boolean,
      advanceOptionsOpen: false,
      PAGE_SEARCHES_SIZE: 100,
      form: AsyncTask.getInstance() as AsyncTask,
      taskCreated: undefined as undefined | boolean,
      taskError: '',
      attachmentIds: [] as String[],
      loading: false as boolean,
      showLimitInfo: false as boolean,
      showOverpassLimitInfo: false as boolean,
      clearAttachmentComponent: false as boolean
    }
  },
  props: {
    userId: {
      type: Number,
      required: true
    },
    totalFills: {
      type: Number,
      default: NUMBER.ZERO
    },
    modalId: {
      type: String,
      default: 'downloadFillsModal'
    },
    templateId: {
      type: Number,
      required: true
    },
    templateName: {
      type: String,
      required: true
    },
    searchIdSelected: {
      type: Number,
      required: true
    },
    searches: {
      type: [] as PropType<CustomSearch[]>,
      required: true
    },
    filters: {
      type: Object,
      required: true
    },
    creatingSearch: {
      required: true
    },
    searchError: {
      type: String
    },
    existTaskInProcess: {
      type: Boolean,
      default: false
    },
    attachmentColumns: {
      type: [] as PropType<Array<AttachmentObject>>,
      required: true
    },
    permissionToSelectAttahments: {
      type: Boolean,
      default: false
    },
    variablesFillsIdent: {
      type: [] as PropType<Array<VariableFill>>,
      required: true
    }
  },
  mounted () {
    this.initForm()
  },
  computed: {
    isSearchIdSelected (): boolean {
      return this.searchIdSelected > 0
    },
    isThereSearch (): boolean {
      let process = this.form.process
      return process && process.search !== undefined && process.search > 0
    },
    selectableSearches (): CustomSearch[] {
      return this.searches.filter((search) => search.origin === SearchOrigin.FILLGRID)
    },
    attachmentSelectionAvailable (): boolean {
      return this.permissionToSelectAttahments && this.attachmentColumns.length > 0
    },
    hasFills () {
      return this.totalFills > 0
    },
    maxLimitFillsOverpass () {
      return this.totalFills > this.FILLS_MAX_LIMIT
    },
    maxLimitFillsWithAttachmentsOverpass () {
      return this.totalFills * (this.form.process?.attachmentIds?.length || 0) > this.FILLS_WITH_ATTACHMENTS_MAX_LIMIT
    },
    maxLimitFillsString () {
      return this.FILLS_MAX_LIMIT.toLocaleString()
    },
    maxLimitFillsWithAttachmentsString () {
      return this.FILLS_WITH_ATTACHMENTS_MAX_LIMIT.toLocaleString()
    },
    blockDownloadButton () {
      return (this.totalFills <= 0) ||
        this.existTaskInProcess ||
        this.loading ||
        this.totalFills > this.FILLS_MAX_LIMIT ||
        this.totalFills * (this.attachmentIds.length || 0) > this.FILLS_WITH_ATTACHMENTS_MAX_LIMIT
    }
  },
  watch: {
    /**
     * Listen to the change in searchIdSelected
     * @param currentSearchId
     * @param oldSearchId
     */
    searchIdSelected (currentSearchId: number, oldSearchId: number) {
      this.setFormSearchId(currentSearchId)
    },
    /**
     * Listen to the change in the searchId into the form object
     *
     * @param currentSearchId current value
     * @param oldSearchId old value
     */
    'form.process.search' (currentSearchId: any, oldSearchId: any) {
      if (!this.form.name) {
        if (currentSearchId === UNDEFINED_ENTITY_ID) this.setTaskName(STRING.EMPTY_STRING)
        else this.proposalTaskNameFromSearches(this.searchIdSelected)
      }
    },
    /**
     * Listen to change the creatingSearch
     * This is used when create a new search from the creation of a new async task
     *
     * @param stillWaiting
     * @param wasWaiting
     */
    creatingSearch (stillWaiting: boolean, wasWaiting: boolean) {
      // If we were waiting and currently no more
      // and we received a value form in the searchId value for the form
      // we try to create the async task, if we were waiting but we
      // didn't receive a searchId value is because its creation failed.
      if (wasWaiting && !stillWaiting && this.isThereSearch) {
        this.createAsyncTask(true)
      } else if (wasWaiting && !stillWaiting && !this.isThereSearch) {
        this.setSearchCreated(false)
      }
    }
  },
  methods: {
    /** Initialize form values */
    initForm (): void {
      this.form.template.id = this.templateId
      this.form.template.name = this.templateName
      this.form.template.label = this.templateName
    },
    /**
     * @param id of the search to set
     */
    setFormSearchId (id: number) {
      this.form.process.search = id
    },
    /** Set a default name for a new search */
    initSearchName () {
      return `Búsqueda_${this.todayFormat()}`
    },
    /**
     * After emits the event for create a new seach with name 'searchName'
     * waits until the form receive a search id or not but the attempt
     * to create the search has finished.
     *
     * @param searchName name for the search to create
     */
    createSearch (searchName: string) {
      const filters = Object.assign({}, this.filters) as FillgridFilters
      filters.name = searchName
      filters.origin = SearchOrigin.DATAFEED // all searches created from this component will be of type DATAFEED
      this.emitCreateSearch(filters)
      this.emitWaitingSearch(true)
    },
    /**
     * Emit the event to update the current searchIdSelected
     * @param searchId id selected
     */
    emitUpdateSearch (searchId?: number) {
      this.$emit('update-search', searchId)
    },
    /**
     * Emit the event to try to create a new search
     * @param filters to create a new search
     */
    emitCreateSearch (filters: FillgridFilters) {
      this.$emit('create-search', filters)
    },
    /**
     * Emit the event to indicate we are waiting for the creation of a new search
     * @param creating flag
     */
    emitWaitingSearch (creating: boolean) {
      this.$emit('waiting-search', creating)
    },
    /**
     * Manage the case to create a new task async
     * When hasn't been selected an already search created,
     * we emit the event to create one and wait for the response,
     * otherwise we create directly a new async task
     */
    manageCreateAsyncTask () {
      this.$emit('check-view-changes')
      if (!this.isThereSearch) {
        const defaultSearchName = this.initSearchName()
        if (!this.form.name) this.proposalTaskName(defaultSearchName)
        this.createSearch(defaultSearchName)
      } else {
        this.createAsyncTask()
      }
    },
    /**
     * Create a new async task to get the current fills
     *
     * @param clearSearch flag to clear the search values settled
     */
    async createAsyncTask (clearSearch: boolean = false) {
      if (this.loading) return
      this.loading = true
      await CatalogsFillsService.createAsyncTask(this.form)
        .then((response) => {
          this.$store.commit(
            'taskNotifications/addTask',
            {
              taskState: AsyncTaskStatus.PROCESSING,
              task: { id: response.id, type: response.type, updateDate: response.updateDate }
            }
          )
          this.showDatafeedToast('Descarga iniciada', response.id, response.template.name, response.updateDate, response.process.requestId)
          this.clearAttachmentComponent = true
          this.closeModal(true)
        })
        .catch((error) => {
          this.setTaskCreated(false)
          let errorData = Object.assign(new APIErrorFormat(), error.response.data)
          if (errorData.isNewErrorFormat()) {
            let blueErrors = errorData.errors
            if (blueErrors.length > 0 && blueErrors[0].code) {
              this.taskError = blueErrors[0].description
            } else {
              this.taskError = blueErrors[0].description
            }
          } else {
            this.taskError = error.response.data.message
          }
        })
        .finally(() => {
          if (clearSearch) {
            this.setFormSearchId(UNDEFINED_ENTITY_ID)
            this.emitUpdateSearch(UNDEFINED_ENTITY_ID)
          }
          this.loading = false
        })
    },
    showDatafeedToast (title: string, id: number, template: string, date: string, requestId: string) {
      const dateOpts: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }
      const dateFormat = Intl.DateTimeFormat('es-MX', dateOpts).format(new Date(`${date}Z`))
      const h = this.$createElement
      const node = h(
        'div', { class: ['d-flex', 'flex-column', 'p-0'] }, [
          h('p', { class: 'm-0' }, [
            h('span', { class: ['font-weight-bold', 'mr-1'] }, `ID:`), `${id}`
          ]),
          h('p', { class: 'm-0' }, [
            h('span', { class: ['font-weight-bold', 'mr-1'] }, `Template:`), template
          ]),
          h('p', { class: 'm-0' }, [
            h('span', { class: ['font-weight-bold', 'mr-1'] }, `Fecha:`), dateFormat
          ]),
          h('p', { class: 'text small m-0' }, [
            h('span', { class: ['font-weight-bold', 'mr-1'] }, `Request:`), requestId
          ])
        ])
      this.$bvToast.toast([node], {
        title: `${title}. [Mis Tareas]`,
        variant: 'success',
        autoHideDelay: 7500,
        solid: true
      })
    },
    /**
     * Emit the close modal event and delete the modal-backdrop,
     * finally reset the form values for the next async task
    */
    closeModal (resetForm?: boolean) {
      this.$emit('close')
      let backdropModal = document.getElementsByClassName('modal-backdrop fade show')[0]
      if (backdropModal) {
        backdropModal.remove()
      }
      this.resetForm(resetForm)
    },
    /** Close the message error in case exist one when we try to create an async task */
    closeTaskError () {
      this.setTaskCreated(undefined)
      this.taskError = ''
    },
    /** Close the message error in case exist one when we try to create a custom search */
    closeSearchError () {
      this.setSearchCreated(undefined)
    },
    /** Clear the form values for the next task */
    resetForm (clearForm?: boolean) {
      if (clearForm) {
        const previousSearchId = this.form.process.search
        const lastTaskName = this.form.name
        this.form = AsyncTask.getInstance()
        this.initForm()
        this.form.process.search = previousSearchId
        const genericSearchNameRegExp = /Búsqueda_\d{8}-\d{6}/i
        if (genericSearchNameRegExp.test(lastTaskName)) {
          this.setTaskName(STRING.EMPTY_STRING)
        } else {
          this.proposalTaskNameFromSearches(this.searchIdSelected)
        }
      }
      this.advanceOptionsOpen = false
      this.setTaskCreated(undefined)
      this.resetCurrentSearchValues()
    },
    /** Clear the values for the new search based on the current search */
    resetCurrentSearchValues () {
      this.setSearchCreated(undefined)
    },
    /**
     * Get the search name from the searches of the user
     *
     * @param searchId to get the search name
     */
    getSearchName (searchId: number) : string {
      let searchName
      if (searchId > 0 && this.searches.length) {
        let searchIndex = this.searches.findIndex((search: CustomSearch) => search.id === searchId)
        searchName = searchIndex > -1 ? this.searches[searchIndex].name : ''
      }
      return searchName || this.searchValues.name
    },
    /**
     * Assign the name to the new async task
     *
     * @param name string to assign
     */
    setTaskName (name: string) : void {
      this.form.name = name
    },
    /**
     * Create and assign a proposal task name
     *  <template_name>_<search_name>_<ddmmyyyy-hhmmss>
     *
     * @param searchId to get its name
     */
    proposalTaskNameFromSearches (searchId?: number): void {
      if (searchId && searchId > 0) {
        this.proposalTaskName(`${this.getSearchName(searchId)}`)
      } else {
        this.proposalTaskName(`${this.searchValues.name}`)
      }
    },
    /**
     * Assign the async task name with the form
     *  <template_name>_<search_name>_<ddmmyyyy-hhmmss>
     *
     * @param searchName proposal name to new task
     */
    proposalTaskName (searchName: string) : void {
      this.setTaskName(`${this.templateName}__${searchName}`)
    },
    /** Create a date format to use into the proposal task name */
    todayFormat (): string {
      let todayDate = new Date()
      let day = todayDate.getDate().toString().padStart(2, '0')
      let month = todayDate.getMonth().toString().padStart(2, '0')
      let year = todayDate.getFullYear()
      let hour = todayDate.getHours().toString().padStart(2, '0')
      let minutes = todayDate.getMinutes().toString().padStart(2, '0')
      let sec = todayDate.getSeconds().toString().padStart(2, '0')
      return `${day}${month}${year}-${hour}${minutes}${sec}`
    },
    setSearchCreated (value: undefined | boolean) {
      this.searchCreated = value
    },
    setTaskCreated (value: undefined | boolean) {
      this.taskCreated = value
    },
    updateAttachmentIds (ids: string[]): void {
      this.attachmentIds = ids
      this.form.process.attachmentIds = ids
    },
    updateAttachmentFormat (formats: AttachmentFormat): void {
      this.form.process.attachmentFormat = formats
    }
  }
})
